import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";

const DARK = ["rgba(255,255,255,0.55)", "#7dd3fc", "#34d399", "#fbbf24", "#f87171"];
const LIGHT = ["rgba(30,41,59,0.6)", "#0369a1", "#047857", "#b45309", "#b91c1c"];

const colorFor = (s, palette) => (s < 2 ? palette[0] : s < 5 ? palette[1] : s < 9 ? palette[2] : s < 14 ? palette[3] : palette[4]);

const sample = (grid, hi, lat, lon) => {
  const { lats, lons, nx, ny, u, v } = grid;
  const fx = ((lon - lons[0]) / (lons[nx - 1] - lons[0])) * (nx - 1);
  const fy = ((lat - lats[0]) / (lats[ny - 1] - lats[0])) * (ny - 1);
  if (!(fx >= 0 && fy >= 0 && fx <= nx - 1 && fy <= ny - 1)) return null;
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const x1 = Math.min(x0 + 1, nx - 1), y1 = Math.min(y0 + 1, ny - 1);
  const tx = fx - x0, ty = fy - y0;
  const at = (xi, yi) => yi * nx + xi;
  const bl = (arr) => {
    const a = arr[at(x0, y0)][hi], b = arr[at(x1, y0)][hi];
    const c = arr[at(x0, y1)][hi], d = arr[at(x1, y1)][hi];
    return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
  };
  return [bl(u), bl(v)];
};

export const WindLayer = ({ grid, ts, theme, enabled }) => {
  const map = useMap();
  const state = useRef({ grid: null, hi: 0, palette: DARK, enabled: true });

  useEffect(() => {
    const g = grid;
    state.current.grid = g;
    state.current.hi = g ? Math.min(g.hours - 1, Math.max(0, Math.round((ts - g.t0) / g.step))) : 0;
    state.current.palette = theme === "dark" ? DARK : LIGHT;
    state.current.enabled = enabled;
  }, [grid, ts, theme, enabled]);

  useEffect(() => {
    const container = map.getContainer();
    const canvas = document.createElement("canvas");
    canvas.className = "wind-canvas";
    container.appendChild(canvas);
    const ctx = canvas.getContext("2d");
    let particles = [];
    let w = 0, h = 0, raf = 0;

    const newP = (age) => ({ x: Math.random() * w, y: Math.random() * h, age, maxAge: 60 + Math.random() * 80 });
    const resize = () => {
      const s = map.getSize();
      w = s.x; h = s.y;
      canvas.width = w; canvas.height = h;
      ctx.clearRect(0, 0, w, h);
      const n = Math.min(1600, Math.max(400, Math.round((w * h) / 1400)));
      particles = Array.from({ length: n }, () => newP(Math.random() * 100));
      canvas.style.opacity = "1";
    };
    const hide = () => {
      canvas.style.opacity = "0";
      ctx.clearRect(0, 0, w, h);
    };

    const frame = () => {
      raf = requestAnimationFrame(frame);
      const { grid: g, hi, palette, enabled: on } = state.current;
      if (!g || !on) {
        if (w && h) ctx.clearRect(0, 0, w, h);
        return;
      }
      ctx.globalCompositeOperation = "destination-in";
      ctx.fillStyle = "rgba(0,0,0,0.92)";
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = "source-over";
      ctx.lineWidth = 1.4;
      const k = Math.min(1.1, 0.22 * Math.pow(2, map.getZoom() - 7));
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (p.age++ > p.maxAge) { particles[i] = newP(0); continue; }
        const ll = map.containerPointToLatLng([p.x, p.y]);
        const uv = sample(g, hi, ll.lat, ll.lng);
        if (!uv) { particles[i] = newP(0); continue; }
        const [u, v] = uv;
        const nx = p.x + u * k, ny = p.y - v * k;
        ctx.strokeStyle = colorFor(Math.hypot(u, v), palette);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(nx, ny);
        ctx.stroke();
        p.x = nx; p.y = ny;
        if (nx < 0 || ny < 0 || nx > w || ny > h) particles[i] = newP(0);
      }
    };

    resize();
    map.on("movestart zoomstart", hide);
    map.on("moveend zoomend resize", resize);
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      map.off("movestart zoomstart", hide);
      map.off("moveend zoomend resize", resize);
      canvas.remove();
    };
  }, [map]);

  return null;
};
